import { Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { ScrapChefUsageDetailedPoint } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { defaultTableSx } from "../tableFormatting";

dayjs.extend(utc);

interface ScrapChefUsageDetailedRow {
  id: number;
  standard: string;
  total_heats: number;
  scrap_chef_heats: number;
  scrap_chef_percentage: number | null;
}

interface ScrapChefUsageDetailedTableProps {
  data: ScrapChefUsageDetailedPoint[];
  selectedDate: Date | null;
}

export const ScrapChefUsageDetailedTable = ({
  data,
  selectedDate,
}: ScrapChefUsageDetailedTableProps) => {
  const { t } = useTenantTranslation();

  const rows = useMemo(
    () => buildRows(data, selectedDate),
    [data, selectedDate]
  );
  const totalsRow = useMemo(
    () => buildTotalsRow(data, selectedDate, t),
    [data, selectedDate, t]
  );
  const columns = useBuildColumns(t);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <HotkeyCompliantDataGrid
        sx={defaultTableSx}
        rows={rows}
        pinnedRows={totalsRow ? { bottom: [totalsRow] } : undefined}
        columns={columns}
      />
    </div>
  );
};

const buildRows = (
  data: ScrapChefUsageDetailedPoint[],
  selectedDate: Date | null
): ScrapChefUsageDetailedRow[] => {
  if (!selectedDate) return [];
  const dateStr = dayjs.utc(selectedDate).format("YYYY-MM-DD");
  return data
    .filter(
      (d) =>
        d.period_start === dateStr && d.standard !== "all" && d.total_heats > 0
    )
    .map((d, i) => ({
      id: i,
      standard: d.standard,
      total_heats: d.total_heats,
      scrap_chef_heats: d.scrap_chef_heats,
      scrap_chef_percentage: d.scrap_chef_percentage,
    }));
};

const buildTotalsRow = (
  data: ScrapChefUsageDetailedPoint[],
  selectedDate: Date | null,
  t: TFunction
): ScrapChefUsageDetailedRow | null => {
  if (!selectedDate) return null;
  const dateStr = dayjs.utc(selectedDate).format("YYYY-MM-DD");
  const allRow = data.find(
    (d) => d.period_start === dateStr && d.standard === "all"
  );
  if (!allRow) return null;
  return {
    id: -1,
    standard: t("Total"),
    total_heats: allRow.total_heats,
    scrap_chef_heats: allRow.scrap_chef_heats,
    scrap_chef_percentage: allRow.scrap_chef_percentage,
  };
};

const useBuildColumns = (t: TFunction): GridColDef[] => {
  const formatNumber = useFormatNumber();

  return [
    {
      field: "standard",
      headerName: t("standard"),
      flex: 1.5,
    },
    {
      field: "total_heats",
      type: "number",
      headerName: t("totalHeats"),
      flex: 1,
      align: "right",
      renderCell: (
        params: GridRenderCellParams<ScrapChefUsageDetailedRow, number>
      ) => (
        <Typography variant="body1" component="div">
          {params.value == null ? "" : formatNumber(0, true)(params.value)}
        </Typography>
      ),
    },
    {
      field: "scrap_chef_heats",
      type: "number",
      headerName: t("scrapChefHeats"),
      flex: 1,
      align: "right",
      renderCell: (
        params: GridRenderCellParams<ScrapChefUsageDetailedRow, number>
      ) => (
        <Typography variant="body1" component="div">
          {params.value == null ? "" : formatNumber(0, true)(params.value)}
        </Typography>
      ),
    },
    {
      field: "scrap_chef_percentage",
      type: "number",
      headerName: `${t("scrapChefUsage")} (%)`,
      flex: 1.2,
      align: "right",
      renderCell: (
        params: GridRenderCellParams<ScrapChefUsageDetailedRow, number>
      ) => (
        <Typography variant="body1" component="div">
          {params.value == null
            ? ""
            : `${formatNumber(1, true)(params.value)}%`}
        </Typography>
      ),
    },
  ];
};
